import * as THREE from '/assets/scripts/three.module.js';
import {
  ORIENTATION_MATRICES_24,
  orientationMatrixForClass,
  nearestOrientationClass,
  composeOrientationClasses
} from './orientation-math.js';
import { MOTHERBOARD_ORIENTATION } from './motherboard-orientation.js?v=20260817-relbridge9';

export { ORIENTATION_MATRICES_24, orientationMatrixForClass, nearestOrientationClass };

// Base topology family only. V1.7 solves in the real chassis frame; mode names such as
// Vertical/Flipped alter component relationships, not the whole case transform.
export function normalizeLayoutFamily(layout) {
  const raw = String(layout || 'reference').trim().toLowerCase();
  if (raw.includes('sandwich')) return 'sandwich';
  if (raw.includes('console')) return 'console';
  if (raw === 'apu' || raw === 'apu_only') return 'reference';
  return 'reference';
}

const PROFILES = Object.freeze({
  case: Object.freeze([
    { id: 0, label: 'UPRIGHT' },
    { id: 2, label: 'INVERTED' },
    { id: 22, label: 'VERTICAL' },
    { id: 16, label: 'VERTICAL REVERSE' }
  ]),
  motherboard: Object.freeze({
    // Manual Orient uses the same chiral rigid-board states as the solver. Rear I/O is the
    // primary physical anchor; PCIe up/down follows from which side the component face uses.
    // Vertical-reference still composes the complete chassis transform on top afterwards.
    reference: Object.freeze([
      { id:MOTHERBOARD_ORIENTATION.componentSideNegativeX.ioRear, label:'TRAY RIGHT · IO REAR · PCIe DOWN' },
      { id:MOTHERBOARD_ORIENTATION.componentSidePositiveX.ioRear, label:'TRAY LEFT · IO REAR · PCIe UP' },
      { id:MOTHERBOARD_ORIENTATION.componentSidePositiveX.ioFront, label:'TRAY LEFT · IO FRONT · PCIe DOWN' },
      { id:MOTHERBOARD_ORIENTATION.componentSideNegativeX.ioFront, label:'TRAY RIGHT · IO FRONT · PCIe UP' }
    ]),
    sandwich: Object.freeze([
      { id:MOTHERBOARD_ORIENTATION.componentSideNegativeX.ioRear, label:'MOBO LEFT · IO REAR · PCIe DOWN' },
      { id:MOTHERBOARD_ORIENTATION.componentSidePositiveX.ioRear, label:'MOBO RIGHT · IO REAR · PCIe UP' },
      { id:MOTHERBOARD_ORIENTATION.componentSidePositiveX.ioFront, label:'MOBO RIGHT · IO FRONT · PCIe DOWN' },
      { id:MOTHERBOARD_ORIENTATION.componentSideNegativeX.ioFront, label:'MOBO LEFT · IO FRONT · PCIe UP' }
    ]),
    console: Object.freeze([
      { id:MOTHERBOARD_ORIENTATION.componentSidePositiveX.ioRear, label:'PANEL LEFT · IO REAR · PCIe UP' },
      { id:MOTHERBOARD_ORIENTATION.componentSideNegativeX.ioRear, label:'PANEL RIGHT · IO REAR · PCIe DOWN' },
      { id:MOTHERBOARD_ORIENTATION.componentSidePositiveX.ioFront, label:'PANEL LEFT · IO FRONT · PCIe DOWN' },
      { id:MOTHERBOARD_ORIENTATION.componentSideNegativeX.ioFront, label:'PANEL RIGHT · IO FRONT · PCIe UP' }
    ])
  }),
  gpu: Object.freeze({
    reference: Object.freeze([
      { id: 17, label: 'IO REAR · FANS DOWN' },
      { id: 21, label: 'IO REAR · FANS UP' },
      { id: 19, label: 'IO FRONT · FANS DOWN' },
      { id: 23, label: 'IO FRONT · FANS UP' }
    ]),
    sandwich: Object.freeze([
      { id: 6, label: 'IO REAR · FANS OUT' },
      { id: 12, label: 'IO REAR · FANS IN' },
      { id: 14, label: 'IO FRONT · FANS OUT' },
      { id: 4, label: 'IO FRONT · FANS IN' }
    ]),
    console: Object.freeze([
      { id: 6, label: 'IO REAR · FACE A' },
      { id: 12, label: 'IO REAR · FACE B' },
      { id: 14, label: 'IO FRONT · FACE A' },
      { id: 4, label: 'IO FRONT · FACE B' }
    ])
  }),
  psu: Object.freeze({
    // PSU manual orientation follows the same axis contract as the solver. All ordinary
    // variants preserve the S5M-style bounding axes and only change facing/sign.
    reference: Object.freeze([
      { id: 17, label: 'CANONICAL · REAR' },
      { id: 19, label: 'CANONICAL · FRONT' },
      { id: 21, label: 'CANONICAL · REAR REVERSE' },
      { id: 23, label: 'CANONICAL · FRONT REVERSE' },
      { id: 16, label: 'FRONT BRACKET · SHALLOW Z' },
      { id: 18, label: 'FRONT BRACKET · SHALLOW Z REVERSE' },
      { id: 20, label: 'FRONT BRACKET · SHALLOW Z FLIP' },
      { id: 22, label: 'FRONT BRACKET · SHALLOW Z FLIP REVERSE' }
    ]),
    sandwich: Object.freeze([
      { id: 17, label: 'CANONICAL · REAR' },
      { id: 19, label: 'CANONICAL · FRONT' },
      { id: 21, label: 'CANONICAL · REAR REVERSE' },
      { id: 23, label: 'CANONICAL · FRONT REVERSE' }
    ]),
    vertical_sandwich: Object.freeze([
      { id: 1, label: 'VERTICAL · REAR' },
      { id: 3, label: 'VERTICAL · FRONT' },
      { id: 9, label: 'VERTICAL · REAR REVERSE' },
      { id: 11, label: 'VERTICAL · FRONT REVERSE' }
    ]),
    console: Object.freeze([
      { id: 17, label: 'CANONICAL · REAR' },
      { id: 19, label: 'CANONICAL · FRONT' },
      { id: 21, label: 'CANONICAL · REAR REVERSE' },
      { id: 23, label: 'CANONICAL · FRONT REVERSE' }
    ])
  }),
  radiator: Object.freeze({
    reference: Object.freeze([{ id: 0, label: 'LONGITUDINAL' }, { id: 4, label: 'TRANSVERSE' }]),
    sandwich: Object.freeze([{ id: 0, label: 'LONGITUDINAL' }, { id: 4, label: 'TRANSVERSE' }]),
    console: Object.freeze([{ id: 0, label: 'LONGITUDINAL' }, { id: 4, label: 'TRANSVERSE' }])
  })
});

export function allowedOrientationProfile(trackType, layout) {
  const type = String(trackType || '').toLowerCase();
  const family = normalizeLayoutFamily(layout);
  if (type === 'case') return [...PROFILES.case];
  if (type === 'motherboard') return [...PROFILES.motherboard[family]];
  if (type === 'gpu') return [...PROFILES.gpu[family]];
  if (type === 'psu') {
    const rawLayout=String(layout || '').toLowerCase();
    if (family === 'sandwich' && rawLayout.includes('vertical')) return [...PROFILES.psu.vertical_sandwich];
    return [...PROFILES.psu[family]];
  }
  if (type === 'radiator') return [...PROFILES.radiator[family]];
  if (type === 'aircooler' || type === 'cooler') return [{ id: null, label: 'SOCKET ROTATION' }];
  return [{ id: 0, label: 'DEFAULT' }];
}

export function defaultOrientationClass(trackType, layout) {
  const profile = allowedOrientationProfile(trackType, layout);
  return Number.isInteger(profile[0]?.id) ? profile[0].id : 0;
}

export function orientationClassFromObject3D(object) {
  object.updateMatrixWorld(true);
  const e = object.matrixWorld.elements;
  return nearestOrientationClass([e[0],e[4],e[8], e[1],e[5],e[9], e[2],e[6],e[10]]);
}

export function applyOrientationClass(object, classId) {
  const m = orientationMatrixForClass(classId);
  const matrix = new THREE.Matrix4();
  matrix.set(
    m[0], m[1], m[2], 0,
    m[3], m[4], m[5], 0,
    m[6], m[7], m[8], 0,
    0, 0, 0, 1
  );
  object.quaternion.setFromRotationMatrix(matrix);
  object.relationalOrientationClass = Number(classId) || 0;
  object.updateMatrixWorld(true);
  return object.relationalOrientationClass;
}

export function cycleAllowedOrientation(object, layout, direction = 1, caseTransformClass = 0) {
  const profile = allowedOrientationProfile(object?.isCase ? 'case' : object?.trackType, layout);
  const usable = profile.filter(entry => Number.isInteger(entry.id)).map(entry => ({
    ...entry,
    id: object?.isCase ? entry.id : composeOrientationClasses(caseTransformClass, entry.id)
  }));
  if (!usable.length) return null;
  const current = Number.isInteger(Number(object.relationalOrientationClass))
    ? Number(object.relationalOrientationClass)
    : orientationClassFromObject3D(object);
  let idx = usable.findIndex(entry => entry.id === current);
  if (idx < 0) idx = 0;
  idx = (idx + (direction >= 0 ? 1 : -1) + usable.length) % usable.length;
  const next = usable[idx];
  applyOrientationClass(object, next.id);
  object.relationalOrientationSource = 'user_profile_adjusted';
  object.relationalOrientationTrainingTier = 'none';
  object.orientationProfileLabel = next.label;
  return next;
}

export function nextCoolerQuarterTurn(cooler, direction = 1) {
  const current = Number(cooler?.socketQuarterTurn || 0);
  cooler.socketQuarterTurn = (current + (direction >= 0 ? 1 : -1) + 4) % 4;
  cooler.relationalOrientationSource = 'user_socket_rotation';
  cooler.relationalOrientationTrainingTier = 'none';
  return cooler.socketQuarterTurn;
}

export function coolerRelativeQuaternion(quarterTurn = 0) {
  return new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), (Number(quarterTurn) % 4) * Math.PI / 2);
}
